<script lang="ts">
	import { onMount } from 'svelte';
	import { format, locale } from 'svelte-i18n';
	import DocumentContextualNavigation from 'src/components/DocumentContextualNavigation.svelte';
	import DocumentContextualNavigationV2 from 'src/components/DocumentContextualNavigationV2.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { localizedLastUpdate } from 'src/helpers/localized_last_update_helper';
	import { toast } from '@Shelly/shelly-design-system';
	import { fetchChangelogVersion, fetchChangelogVersions } from 'src/services/changelog';
	import type { ChangelogVersionMeta, ReleaseVersion } from 'src/types/Changelog';
	import { buildVersionSection } from 'src/helpers/changelog_helpers';

	let versionMetas: ChangelogVersionMeta[] = [];
	let selectedAppVersion: string | null = null;
	let selectedVersion: ReleaseVersion | null = null;
	let isLoadingContent = false;

	const cache = new Map<string, ReleaseVersion>();
	let requestSeq = 0;

	const cacheKey = (appVersion: string, locale: string) => `${appVersion}|${locale}`;

	const showError = () =>
		toast.open({
			type: 'danger',
			title: $format('error_global', { default: 'Something went wrong!' }),
		});

	const loadVersions = async () => {
		try {
			const list = await fetchChangelogVersions();
			versionMetas = list;
			if (list.length > 0 && !selectedAppVersion) {
				selectedAppVersion = list[0].appVersion;
			}
		} catch {
			showError();
		}
	};

	const loadVersionData = async (appVersion: string, locale: string) => {
		const key = cacheKey(appVersion, locale);
		const cached = cache.get(key);

		const reqId = ++requestSeq;

		if (cached) {
			selectedVersion = cached;
			isLoadingContent = false;
			return;
		}

		isLoadingContent = true;
		try {
			const data = await fetchChangelogVersion(appVersion, locale);
			if (reqId !== requestSeq) return;
			if (!data) {
				showError();
				selectedVersion = null;
				return;
			}
			cache.set(key, data);
			selectedVersion = data;
		} catch {
			if (reqId !== requestSeq) return;
			showError();
			selectedVersion = null;
		} finally {
			if (reqId === requestSeq) isLoadingContent = false;
		}
	};

	onMount(() => {
		loadVersions();
	});

	/**
	 * Track the displayed UI locale rather than `user_settings.lang`. The two can
	 * drift apart (e.g. token refresh sets the i18n locale to the persisted user
	 * profile lang without updating user_settings), and the changelog content
	 * must always match the language the user is actually seeing.
	 */
	$: if (selectedAppVersion && $locale) {
		loadVersionData(selectedAppVersion, $locale);
	}

	$: menuItems = versionMetas.map((version) => ({
		id: `version-${version.appVersion}`,
		label: `${$format('title_app_version', { default: 'App version' })} ${version.appVersion}`,
		active: version.appVersion === selectedAppVersion,
	}));

	$: sectionsContent = selectedVersion ? [buildVersionSection(selectedVersion)] : [];

	$: latestMeta = versionMetas[0];
	$: latestVersion = localizedLastUpdate(
		latestMeta ? latestMeta.timestamp * 1000 : null,
		$locale,
		'privacy_policy_last_update',
		$format,
	);

	$: title = $format('tab_changelog', { default: 'Changelog' });

	const onSectionChange = (event: CustomEvent<string>) => {
		const id = event.detail;
		const prefix = 'version-';
		if (typeof id !== 'string' || !id.startsWith(prefix)) return;
		const next = id.slice(prefix.length);
		if (next !== selectedAppVersion) {
			selectedAppVersion = next;
		}
	};
</script>

{#if $showNewLayout}
	<DocumentContextualNavigationV2
		{title}
		{menuItems}
		{sectionsContent}
		{latestVersion}
		mode="single"
		loadingContent={isLoadingContent}
		on:section-change={onSectionChange}
	/>
{:else}
	<DocumentContextualNavigation
		{title}
		{menuItems}
		{sectionsContent}
		{latestVersion}
		mode="single"
		loadingContent={isLoadingContent}
		on:section-change={onSectionChange}
	/>
{/if}
